import type { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import {
	clearGuestSession,
	getGuestSession,
	guestOrderPath,
	guestQuotePath,
} from '../../lib/guest-session';
import { readGuestScope } from '../../lib/guest-token';

/**
 * Keeps a guest out of the rest of the platform.
 *
 * A guest link produces a normal client session, so every platform route would
 * otherwise open to them — and did. The scope now comes off the ACCESS TOKEN
 * (`guestQuoteId`), not the sessionStorage marker: the session travels in a
 * cookie shared by every tab on this origin, while the marker does not, so a
 * guest opening `/quotes` in a second tab restored the same session, found no
 * marker, and got the whole app. The token reaches every tab the session does.
 *
 * Still presentation scoping, not authorization — the claim is read, never
 * verified. What a guest may actually READ has to be enforced server-side; this
 * only decides which screens we draw.
 */
export function GuestGate({ children }: { children: ReactNode }) {
	const { token } = useAuth();
	const scope = readGuestScope(token);
	const session = getGuestSession();

	// A normal client session, in a tab still holding a marker from a guest who
	// used it earlier. The session is the truth; drop the stale marker.
	if (!scope) {
		if (session) clearGuestSession();
		return <>{children}</>;
	}

	// Once they've accepted, the order is the live thing — the quote behind it
	// is closed, so landing back there would look like a dead end. Only this tab
	// knows which orders came out of the acceptance, which is what the marker is
	// still good for; another tab simply lands on the quote.
	const sameQuote = session?.orderQuoteId === scope.quoteId;
	const latestOrderId = sameQuote ? session?.orderIds?.[session.orderIds.length - 1] : undefined;

	return (
		<Navigate
			to={latestOrderId ? guestOrderPath(latestOrderId) : guestQuotePath(scope.quoteId)}
			replace
		/>
	);
}
